import { Navigate, Outlet, useLocation } from 'react-router-dom'
import { HandoffAlertBanner } from '@/components/HandoffAlertBanner'
import { Sidebar } from '@/components/Sidebar'
import { Topbar } from '@/components/Topbar'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { useAuthStore } from '@/stores/auth'

// 2026-07-21 (user request) — role=vendedor only gets Conversations; every
// other admin route redirects there. This is UI convenience only — the real
// enforcement is server-side (row-level scoping added this same session in
// conversations.ts/dashboard.ts/ghl-calendars.ts); a vendedor hitting the API
// directly is still blocked/scoped regardless of what the UI lets them
// navigate to.
const VENDEDOR_ALLOWED_PATHS = ['/admin/conversations', '/conversations']

function isVendedorAllowedPath(pathname: string): boolean {
  return VENDEDOR_ALLOWED_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`))
}

// Story 12.8 (Epic E12) — same UI-convenience-only restriction as vendedor
// above: an sdr only ever reads/acts on her own carteira (Stories 12.4/12.5),
// server-scoped regardless of what the UI lets her navigate to. Dashboard
// carteira scoping (Story 12.7) isn't built yet, so it's deliberately NOT in
// this allow-list — add it there once it ships.
const SDR_ALLOWED_PATHS = ['/admin/conversations', '/conversations']

function isSdrAllowedPath(pathname: string): boolean {
  return SDR_ALLOWED_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`))
}

/**
 * Authenticated layout. Redirects to /login if no token in store. Story 1.7
 * adds `useCurrentUser()` which calls `GET /admin/auth/me` and keeps the
 * store in sync (also acts as the canonical "is the token still valid?"
 * probe — the 401 interceptor in `lib/api.ts` handles expiry).
 */
export function AdminLayout(): React.ReactElement {
  const token = useAuthStore((s) => s.token)
  const role = useAuthStore((s) => s.user?.role)
  const location = useLocation()

  // Always invoke the hook (React rules); the hook itself early-returns
  // when there is no token.
  useCurrentUser()

  if (!token) {
    return <Navigate to="/login" replace />
  }

  if (role === 'vendedor' && !isVendedorAllowedPath(location.pathname)) {
    return <Navigate to="/admin/conversations" replace />
  }

  if (role === 'sdr' && !isSdrAllowedPath(location.pathname)) {
    return <Navigate to="/admin/conversations" replace />
  }

  return (
    <div className="flex h-screen w-full overflow-hidden bg-background text-foreground">
      <Sidebar />
      <div className="flex flex-1 flex-col">
        <Topbar />
        <HandoffAlertBanner />
        <main id="main" tabIndex={-1} className="flex-1 overflow-y-auto p-4 md:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
